import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import LoginLeftBg from "../../assets/images/login-left-bg.png";
import LoginRightTopBg from "../../assets/images/login-right-top-bg.png";
import LoginRightBottomBg from "../../assets/images/Right-bottom.png";
import signLogo from "../../assets/images/signLogo.png";
import forgotPassword2 from "../../assets/images/forgotPassword2.png";
import { routes } from "../../helpers/constants";
import { EmailConfigurationIcon, emailIcon } from "../../assets/icons";
import {
  reqtoTwoFASetup,
  reqtoTwoFAVerify,
} from "../../redux/slices.js/authentication/authenticationSlice";
import { notify } from "../../helpers/commonFunctions";

function SetUpAuthentication() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setupData, setupStatus } = useSelector(
    (state) => state.authentication
  );

  const [step, setStep] = useState("setup"); // "setup" → "recovery"
  const [token, setToken] = useState("");
  const inputRefs = useRef([]);

  useEffect(() => {
    // fetch setup data on mount
    dispatch(reqtoTwoFASetup());
  }, [dispatch]);

  /** VERIFY OTP */
  const handleVerify = async () => {
    if (token.length !== 6) {
      notify("Please enter a valid 6-digit OTP.");
      return;
    }
    try {
      const result = await dispatch(reqtoTwoFAVerify({ token })).unwrap();
      if (result?.status) {
        setStep("recovery");
      } else {
        notify("Invalid OTP, please try again.");
      }
    } catch {
      notify("Verification failed. Please try again.");
    }
  };

  /** Download + Continue → Dashboard */
  const handleDownloadAndContinue = () => {
    if (setupData?.backup_codes?.length) {
      const blob = new Blob([setupData.backup_codes.join("\n")], {
        type: "text/plain",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "backup_codes.txt";
      a.click();
      URL.revokeObjectURL(url);
    }
    navigate(routes.dashboard);
  };

  return (
    <div className="signup-page bg-white">
      <div className="container">
        <div className="signup-container h-screen flex items-center flex-wrap relative">
          {/* Left panel */}
          <div className="py-3 w-full lg:w-1/2 h-full hidden lg:flex">
            <div className="signup-left w-full h-full lg:flex items-start justify-between flex-col bg-blue relative rounded-[30px]">
              <img
                src={LoginLeftBg}
                alt=""
                className="absolute left-0 top-0 z-0"
              />
              <img
                src={LoginRightTopBg}
                alt=""
                className="absolute right-0 top-0 z-0"
              />
              <img
                src={LoginRightBottomBg}
                alt=""
                className="absolute right-0 bottom-0 z-0"
              />
              <div className="signUpText flex flex-col gap-5 ps-[45px] pt-[35px]">
                <img src={signLogo} className="max-w-[168px]" alt="" />
              </div>
              <img
                src={forgotPassword2}
                alt="otp"
                className="mb-0 object-contain w-auto h-[70%] z-0"
              />
            </div>
          </div>

          {/* Right panel */}
          <div className="signup-right overflow-auto w-full lg:w-1/2 mx-auto md:px-[70px] h-full py-10 flex items-center justify-center flex-col px-4">
            <div className="signup-right-title pb-[43px]">
              <div className="w-[84px] h-[84px] border-[16px] border-[#E3EAF9] rounded-full flex items-center justify-center mx-auto mb-[20px]">
                {emailIcon}{" "}
              </div>
              {step === "setup" ? (
                <>
                  <h1 className="font-roboto mb-2 text-center text-black inner-title">
                    Set Up Two-Factor Authentication
                  </h1>
                  <p className="w-[100%] text-p mb-0 text-gray w-80 text-center m-auto">
                    Scan the QR code or enter the secret key in your
                    authenticator app, then enter the 6-digit code below.
                  </p>
                </>
              ) : (
                <>
                  <h1 className="font-roboto mb-2 text-center text-black inner-title">
                    Recovery Codes
                  </h1>
                  <p className="text-p mb-0 text-gray w-80 text-center">
                    Please save these codes in a secure place. You’ll need them
                    if you lose access to your authenticator app.
                  </p>
                </>
              )}
            </div>

            <div className="w-full lg:max-w-lg space-y-4 pb-5">
              {setupStatus === "loading" ? (
                <p className="text-gray-600 text-center">
                  Loading setup data...
                </p>
              ) : step === "setup" ? (
                <>
                  {/* QR + Secret */}
                  {setupData && (
                    <>
                      <div className="flex flex-col md:flex-row gap-6 justify-center">
                        <div className="text-center">
                          <p className="font-semibold mb-2">
                            Scan this QR code:
                          </p>
                          <div className="p-3 bg-gray-50 rounded-xl border inline-block">
                            <img
                              src={setupData.qr}
                              alt="2FA QR"
                              className="w-40 h-40"
                            />
                          </div>
                        </div>
                      </div>
                      <div className="text-center md:text-left">
                        <p className="flex justify-center font-semibold mb-2">
                          Or enter this secret key:
                        </p>
                        <code className="flex justify-center block bg-gray-100 text-sm p-3 rounded-md border">
                          {setupData.secret}
                        </code>
                      </div>
                    </>
                  )}

                  {/* OTP input */}
                  <div className="flex items-center justify-center gap-4">
                    {[...Array(6)].map((_, index) => (
                      <input
                        key={index}
                        ref={(el) => (inputRefs.current[index] = el)}
                        maxLength="1"
                        type="text"
                        inputMode="numeric"
                        className="!w-12 !h-12 text-center border rounded !text-[24px] font-semibold focus:outline-none !border-gray-300 focus:!border-black"
                        value={token[index] || ""}
                        onChange={(e) => {
                          const val = e.target.value.replace(/[^0-9]/g, "");
                          let newOtp = token.split("");
                          newOtp[index] = val;
                          setToken(newOtp.join(""));
                          if (val && inputRefs.current[index + 1]) {
                            inputRefs.current[index + 1].focus();
                          }
                        }}
                        onKeyDown={(e) => {
                          if (
                            e.key === "Backspace" &&
                            !token[index] &&
                            index > 0
                          ) {
                            inputRefs.current[index - 1]?.focus();
                          }
                        }}
                      />
                    ))}
                  </div>

                  <div className="pt-5 text-center">
                    <button onClick={handleVerify} className="btn-yellow">
                      Verify & Continue
                    </button>

                    <div className="text-center md:text-left mt-2">
                      <p
                        className="flex justify-center font-semibold mb-2 cursor-pointer text-blue underline"
                        onClick={() => navigate(routes.dashboard)}
                      >
                        Skip
                      </p>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Recovery Codes */}
                  {setupData?.backup_codes?.length > 0 && (
                    <div className="bg-gray-100 p-4 rounded-md max-h-48 overflow-y-auto space-y-1">
                      {setupData.backup_codes.map((code, i) => (
                        <div key={i} className="font-mono text-sm">
                          {code}
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="pt-[52px] text-center">
                    <button
                      onClick={handleDownloadAndContinue}
                      className="btn-yellow"
                    >
                      Download & Continue
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SetUpAuthentication;
